import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://filtrolens.com";

interface SeoProps {
  title: string;
  description: string;
  type?: "website" | "article" | "product";
  jsonLd?: Record<string, unknown>;
  noindex?: boolean;
}

const Seo = ({ title, description, type = "website", jsonLd, noindex }: SeoProps) => {
  const { pathname } = useLocation();
  const url = `${SITE_URL}${pathname === "/" ? "/" : pathname.replace(/\/$/, "")}`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type === "product" ? "website" : type} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {noindex && <meta name="robots" content="noindex" />}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

const ROUTES: Record<string, { title: string; description: string; noindex?: boolean }> = {
  "/": { title: "FiltroLens — Custom AR Filters for Instagram, TikTok & Snapchat", description: "FiltroLens is an AR studio crafting premium custom filters for creators and brands on Instagram, TikTok and Snapchat. Browse filters or order your own." },
  "/filters": { title: "AR Filter Gallery — Browse FiltroLens Effects", description: "Browse the FiltroLens collection of face filters, world effects and interactive AR games, searchable by category." },
  "/special-orders": { title: "Order a Custom AR Filter — FiltroLens", description: "Request a custom AR filter for your brand, event or profile. Share your idea and our artists will design, build and deliver it." },
  "/join-us": { title: "Join the FiltroLens Team — AR Creator Jobs", description: "Apply to join FiltroLens as an AR creator, 3D modeler or digital artist and build filters for brands worldwide." },
  "/about": { title: "About FiltroLens — Our Story & Mission", description: "Meet FiltroLens, a team of digital artists and AR creators building premium augmented reality filters for creators and brands." },
  "/contact": { title: "Contact FiltroLens — Get in Touch", description: "Contact FiltroLens by email, phone or WhatsApp for custom AR filter projects, partnerships and support." },
  "/privacy": { title: "Privacy Policy — FiltroLens", description: "How FiltroLens collects, uses and protects your personal data, including cookies and analytics." },
  "/terms": { title: "Terms of Service — FiltroLens", description: "The terms and conditions for using FiltroLens, ordering custom AR filters and licensing our effects." },
  "/faq": { title: "AR Filter FAQ — Pricing, Delivery & Usage Rights | FiltroLens", description: "Answers to common questions about AR filters, custom orders, pricing, delivery times and usage rights at FiltroLens." },
  "/blog": { title: "AR Filter Blog — Guides & Insights | FiltroLens", description: "Guides on augmented reality filters, how they work, and how brands use AR for social media marketing." },
  "/admin/login": { title: "Admin Login — FiltroLens", description: "FiltroLens administrator sign in.", noindex: true },
  "/admin": { title: "Admin Dashboard — FiltroLens", description: "FiltroLens administration.", noindex: true },
};

/** Sets default per-route metadata; dynamic pages render their own <Seo> which overrides it. */
export const RouteSeo = () => {
  const { pathname } = useLocation();
  const meta = ROUTES[pathname.replace(/(.)\/$/, "$1")];
  if (!meta) return null;
  return <Seo {...meta} />;
};

export default Seo;
